import { useEffect, useState } from 'react';

import { IncomingCommand } from './types/command';
import {
  ListenerSignature,
  SocketConnectionContextValue,
  SubscribeSignature,
  UnsubscribeSignature,
} from './types/context';
import { SocketService } from './types/socket';
import { isHeartbeatMessage } from './types/guards';

type Props = {
  connection: SocketService;
};

export const useSocketConnection = ({ connection }: Props): SocketConnectionContextValue => {
  const [value, setValue] = useState<SocketConnectionContextValue>({
    send: () => void 0,
    subscribe: () => () => void 0,
    unsubscribe: () => void 0,
  });

  useEffect(() => {
    const commandListeners = new Map<IncomingCommand, ListenerSignature[]>();

    const subscribe: SubscribeSignature = (command: any, listener: any) => {
      if (!commandListeners.has(command)) {
        commandListeners.set(command, []);
      }

      commandListeners.get(command).push(listener);

      return () => unsubscribe(command, listener);
    };

    const unsubscribe: UnsubscribeSignature = (command: any, listener: any) => {
      const updatedListeners = commandListeners.get(command)?.filter((l) => l !== listener) || [];
      commandListeners.set(command, updatedListeners);
    };

    const messageHandler = (event: any) => {
      if (isHeartbeatMessage(event)) {
        return;
      }

      commandListeners.get(event.command)?.forEach((listener) => listener(event));
    };

    const closeHandler = () => {
      messageHandler({
        command: IncomingCommand.SOCKET_CLOSE,
      });
    };

    const openHandler = () => {
      messageHandler({
        command: IncomingCommand.SOCKET_OPEN,
      });
    };

    const reconnectHandler = () => {
      messageHandler({
        command: IncomingCommand.SOCKET_RECONNECT,
      });
    };

    connection.onClose(closeHandler);
    connection.onMessage(messageHandler);
    connection.onOpen(openHandler);
    connection.onReconnect(reconnectHandler);

    setValue({
      send: connection.send.bind(connection),
      subscribe,
      unsubscribe,
    });

    return () => {
      connection.removeListener('onClose', closeHandler);
      connection.removeListener('onMessage', messageHandler);
      connection.removeListener('onOpen', openHandler);
      connection.removeListener('onReconnect', reconnectHandler);
    };
  }, [connection]);

  return value;
};
